import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { NgbDateStruct } from "@ng-bootstrap/ng-bootstrap";
import { ColDef } from "ag-grid-enterprise";
import { DropDownOption } from "src/app/product-capture/model/interfaces/drop-down-option.interface";
import { TypeToForm } from "src/app/utils/types.utils";
import { DistributorGroupsContact } from "../distributor.const";
import { MasterAgreement } from "../master-agreements/master-agreements.consts";
import { labelsToColumns } from "../shared/fees-management.utils";
import { SupplementAgreementFee, SupplementAgreementFeeType } from "./supplement-agreement-fee.model";

export const SUPPLEMENT_AGREEMENTS_COLUMNS: ColDef[] = labelsToColumns('supplementAgreement', ['fees']);

export enum SupplementAgreementStatus {
    IN_PROGRESS = 'In Progress',
    WITH_OPS_TEAM = 'With Ops Team',
    NO_FEES_FOUND = 'No Fees Found',
    WITH_SALES = 'With Sales',
    WITH_COMPLIANCE = 'With Compliance',
    WITH_LEGAL = 'With Legal',
    WITH_CLIENT_FOR_EXECUTION = 'With Client For Execution',
    FULLY_EXECUTED = 'Fully Executed',
    FULLY_EXECUTED_WITH_EXCEPTION = 'Fully Executed With Exception',
    TERMINATED = 'Terminated',
}

export enum SupplementAgreementRegion {
    A_NZ = 'A&NZ',
    AMERICAS = 'Americas',
    ASIA_PAC = 'Asia-Pac',
    EMEA = 'EMEA',
    JAPAN = 'Japan' 
}

export enum SupplementAgreementBusiness {
    CMTS_CEEMEA = 'CMTS - CEEMEA',
    CMTS_GERMANY = 'CMTS - Germany',
    CMTS_W_EUROPE = 'CMTS - W Europe',
    GCG = 'GCG',
    GTB = 'GTB',
    ICG = 'ICG',
    OTHER = 'Other',
    STRUCTURED_FINANCE = 'Structured Finance',
    STRUCTURING = 'Structuring',
    TRADING = 'Trading ',
}

export enum BusinessDaysConvention {
    PRECEDING = 'Preceding',
    FOLLOWING = 'Following',
}

export enum PaymentFrequency {
    MONTHLY = 'Monthly',
    QUARTERLY = 'Quarterly',
    SEMIANNUALLY = 'Semiannually',
    ANNUAL = 'Annual',
}

export enum FrequencyValues {
    MONTHLY = '1M',
    QUARTERLY = '3M',
    SEMIANNUALLY = '6M',
    ANNUAL = '1Y',
}

export type FrequencyValuesKeys = { [key in FrequencyValues]: PaymentFrequency };

function frequencyValuesToPaymentFrequency(): FrequencyValuesKeys {
    const frequencies = Object.entries(FrequencyValues).reduce<FrequencyValuesKeys>((acc, [key, value]) => {
        const paymentValue = (PaymentFrequency as any)[key];
        if (paymentValue) {
          acc[value] = paymentValue;
        }
        return acc;
      }, {} as FrequencyValuesKeys);

    return frequencies;
}

export const MappedFrequency: FrequencyValuesKeys = frequencyValuesToPaymentFrequency();

export enum SupplementAgreementCalculationMethod {
    // AVERAGE_PRICE = 'Average Price', (For day 2)
    BALANCE_ISSUE_PRICE_RATE = 'Balance Issue Price Rate',
    BALANCE_PRICE_RATE = 'Regular Calculation',
    DAILY_ACCRUING = 'Daily Accruing',
    EXOTIC = 'Exotic',
    // INTERVAL_RATE = 'Interval Rate', (For day 2)
}

export enum SupplementAgreementBalanceSource {
    CUSTODY_UPLOAD = 'Custody Upload',
    DISTRIBUTOR = 'Distributor',
    DISTRIBUTOR_DOWNLOAD = 'Distributor Download',
    SALES = 'Sales',
    SPECIFY_EMAIL = 'Specify E-Mail',
    TOTAL_OUTSTANDING = 'Total Outstanding',
    TRADER = 'Trader ',
}

export enum AllowedFeeTypes {
    TRAILER_FEE = 'Trailer Fee',
    NO_PAYMENT_FEE = 'No Payment Fee',
    UPFRONT_FEE = 'Upfront Fee',
}

export enum InducementDisclosedToInvestorMethod {
    AGREEMENT = 'Agreement',
    KID = 'KID',
    OTHER = 'Other',
    PIB = 'PIB',
    PRODUCT_DOCUMENTATION = 'Product Documentation',
    PROSPECTUS = 'Prospectus',
    SWISS_SIMPLIFIED_PROSPECTUS = 'Swiss Simplified Prospectus',
    TERMSHEET = 'Termsheet',
    TRANSACTION_DOCS = 'Transaction Docs',
}

export enum ValueAddedForInvestor {
    INVESTMENT_ADVISORY_FOR_INVESTOR = 'Investment Advisory for Investor',
    PORTFOLIO_MANAGEMENT_FOR_INVESTOR = 'Portfolio Management for Investor',
    PRODUCT_ANALYSIS_FOR_INVESTOR = 'Product Analysis for Investor',
    PRODUCT_SERVICE_PLATFORM = 'Product Service Platform',
    PRODUCT_STRUCTURING_FOR_INVESTOR = 'Product Structuring for Investor',
    OTHER = 'Other ',
}

export type SupplementAgreementStatusKeys = keyof typeof SupplementAgreementStatus;

export type AllowedFeeTypesKeys = keyof typeof AllowedFeeTypes;

export type SupplementAgreementCalculationMethodKeys = keyof typeof SupplementAgreementCalculationMethod;

export type SupplementAgreementBalanceSourceKeys = keyof typeof SupplementAgreementBalanceSource;

export interface SupplementAgreement {
    id?: number;
    supplementAgreementId?: number;
    masterAgreement?: MasterAgreement;
    allowedFeeTypes: AllowedFeeTypesKeys[];
    distributorId: number;
    agreementRequestDate?: string; // Start as today's date
    agreementExecutionDate?: string;
    agreementStatus?: SupplementAgreementStatusKeys;
    holdingEmailsTo?: DistributorGroupsContact[];
    setToFullyExecuted?: string;
    setToTerminated?: string;
    opsContactId?: number;
    opsApproverContactId?: number; // Users with Sales as a desk on contact record;
    salesContactId?: number;
    productCategory?: number; // Static data from Product Category - This table is not mapped; - Not for day one
    region?: SupplementAgreementRegion; // Static data - Is this multiple choice? - This table is not mapped;
    business?: SupplementAgreementBusiness; // Static data - Is this multiple choice? - This table is not mapped;
    inducementDisclosedToInvestorMethod?: InducementDisclosedToInvestorMethod; // Static data from Inducement Disclosed to Investor Method - This table is not mapped;
    valueAddedForInvestor?: ValueAddedForInvestor; // Static data from Value Added for Investor - This table is not mapped;
    recurringFeeConfigId?: number;
    businessDayConvention?: `${BusinessDaysConvention}`;
    paymentFrequency?: `${FrequencyValues}`;
    evaluationFrequency?: `${FrequencyValues}`;
    evaluationDay?: string;
    calculationMethod?: SupplementAgreementCalculationMethodKeys;
    paymentAdvice?: boolean;
    evaluationStartDate?: string | NgbDateStruct;
    evaluationEndDate?: string | NgbDateStruct;
    startOfFirstPeriod?: string | number;
    evaluationPeriodType?: string;
    evaluationDates?: string[];
    balanceSource?: SupplementAgreementBalanceSourceKeys;
    balancesEmail?: string;
    rcsPnlEUR?: number;
    exoticsComments?: string;
    exoticsCommentForClientStatement?: string;
    comments?: string;
    holdingPeriodApplicable?: boolean;
    hedgeFundAndOrCreditRelated?: boolean;
    additionalAlternativeProvisionRequired?: boolean;
    fees?: SupplementAgreementFee[];
    createdBy?: string;
    createdDate?: string;
    lastUpdatedBy?: string;
    updatedOn?: string;
    inducementDisclosedToInvestorMethodOther?: string;
    valueAddedForInvestorOther?: string;
}

export type SupplementAgreementForm = TypeToForm<SupplementAgreement>;

const fb = new FormBuilder();

export function buildSupplementAgreementForm(): FormGroup<SupplementAgreementForm> {
    return fb.group<SupplementAgreementForm>({
        id: fb.control(null),
        masterAgreement: fb.control(null, Validators.required),
        distributorId: fb.control(null, Validators.required),
        allowedFeeTypes: fb.control<AllowedFeeTypes[]>([]),
        agreementExecutionDate: fb.control<string>(null),
        agreementRequestDate: fb.control<string>(null),
        agreementStatus: fb.control('WITH_OPS_TEAM'),
        balancesEmail: fb.control(null),
        balanceSource: fb.control(null),
        business: fb.control(null),
        businessDayConvention: fb.control(null),
        calculationMethod: fb.control(null),
        comments: fb.control(null),
        evaluationDay: fb.control(null),
        evaluationDates: fb.control<string[]>([]),
        evaluationEndDate: fb.control(null),
        evaluationPeriodType: fb.control(null),
        evaluationStartDate: fb.control(null),
        exoticsComments: fb.control(null),
        exoticsCommentForClientStatement: fb.control(null),
        holdingEmailsTo: fb.control(null),
        inducementDisclosedToInvestorMethod: fb.control(null),
        opsApproverContactId: fb.control(null),
        opsContactId: fb.control(null),
        paymentAdvice: fb.control(false),
        paymentFrequency: fb.control(null),
        evaluationFrequency: fb.control(null),
        productCategory: fb.control(null),
        recurringFeeConfigId: fb.control(null),
        region: fb.control(null),
        rcsPnlEUR: fb.control(null),
        salesContactId: fb.control(null),
        setToFullyExecuted: fb.control(null),
        setToTerminated: fb.control(null),
        startOfFirstPeriod: fb.control(null),
        valueAddedForInvestor: fb.control(null),
        additionalAlternativeProvisionRequired: fb.control(false),
        hedgeFundAndOrCreditRelated: fb.control(false),
        holdingPeriodApplicable: fb.control(false),
        valueAddedForInvestorOther: fb.control(null),
        inducementDisclosedToInvestorMethodOther: fb.control(null),
    });
}

export enum FeeCategory {
    // Trailer Fee categories
    TRAILER_FEE = 'Trailer Fee',
    MANAGEMENT_FEE = 'Management Fee',
    PLACEMENT_FEE = 'Placement Fee',
    REBATE = 'Rebate',

    // Upfront Fee categories
    PRIMARY_MARKET = 'Primary Market',
    SECONDARY_MARKET = 'Secondary Market',
    ARRANGER_FEE = 'Arranger Fee',
    FRONT_END_LOAD_REFUNDED = 'Front End Load Refunded',
    INTRODUCING = 'Introducing',
    COST_OF_PURCHASERS_REGISTER = 'Cost of Purchasers Register',
    PLATFORM_SERVICE_FEE = 'Platform Service Fee',
    SECONDARY_MARKET_LIST = 'Secondary Market List',
    STRUCTURING = 'Structuring',
    TRANSACTION = 'Transaction',
   

    // No Payment Fee categories
    DISCOUNT_ON_PRIMARY_MARKET_PRICE = 'Discount on Primary Market Price',
    DISCOUNT_ON_SECONDARY_MARKET = 'Discount on Secondary Market',
    FRONT_END_LOAD_LEVIED_BY_DISTRIBUTOR = 'Front End Load Levied by Distributor',
    HOLDING_PERIOD__EXIT_FEE = 'Holding Period / Exit Fee',
}

type KeysOfFeeCategory = keyof typeof FeeCategory;

function mapFeesCategoriesToDropdown(categories: KeysOfFeeCategory[]): DropDownOption[] {
    return categories.map(category => ({ id: category, displayValue: FeeCategory[category] }));
}

export const FEES_CATEGORIES: { 
    [K in SupplementAgreementFeeType]: DropDownOption[]
} = {
    TRAILER_FEE: mapFeesCategoriesToDropdown([
        'TRAILER_FEE',
        'MANAGEMENT_FEE',
        'PLACEMENT_FEE',
        'REBATE',
    ]),
    UPFRONT_FEE: mapFeesCategoriesToDropdown([
        'PRIMARY_MARKET',
        'SECONDARY_MARKET',
        'ARRANGER_FEE',
        'FRONT_END_LOAD_REFUNDED',
        'INTRODUCING',
        'COST_OF_PURCHASERS_REGISTER',
        'PLACEMENT_FEE',
        'PLATFORM_SERVICE_FEE',
        'SECONDARY_MARKET_LIST',
        'STRUCTURING',
        'TRANSACTION',
    ]),
    NO_PAYMENT_FEE: mapFeesCategoriesToDropdown([
        'DISCOUNT_ON_PRIMARY_MARKET_PRICE',
        'DISCOUNT_ON_SECONDARY_MARKET',
        'FRONT_END_LOAD_LEVIED_BY_DISTRIBUTOR',
        'HOLDING_PERIOD__EXIT_FEE',
    ]),
}

export type DocumentApprover = {
    approverType: string;
    approverUsername: string;
    approverEmail: string;
    createdAt: string;
    updatedAt: string;
    group: string;
};
  
export type SupplementAgreementDocument = {
    id: number;
    version?: number;
    createdBy: string;
    language: string;
    fileUrl: string;
    status: string;
    type: string;
    createdAt: string;
    updatedAt: string;
    subtype: string;
    documentState: string;
    salesApprover: DocumentApprover;
    tradingApprover: DocumentApprover;
    format: string;
}

export type SupplementAgreementGenerateDocumentPayload = {
    supplementAgreementId: number;
    language: string;
    docFormat: string;
}

export type RollDayConfig = {
    id?: number;
    configBaseType?: string;
    rollDayConfigType?: string;
    configBaseId?: number; // SupplementAgreementFee id;
    rollDay?: string;
    irregularPeriod?: string;
    endFirstPeriod?: string;
    startLastPeriod?: string;
}

export type SupplementAgreementSchedulePayload = {
    startDate: string;
    endDate: string;
    frequency: string;
    rollConfig: RollDayConfig;
    rollConvention: string;
    periodInclusion: string;
    shiftStartPeriodDate: boolean;
    shiftEndPeriodDate: boolean;
    calculationDateLag: string;
    caculationDateLagBase: string;
    dayCountBasis: string;
    determinationDateAdjustmentCalendars: string[];
}

export type SupplementAgreementSchedule = {
    schedule: {
        period: number;
        calculationPeriodStart: string;
        calculationPeriodEnd: string;
        calculationDate: string;
        determinationDate: string;
        periodInclusion: string;
        dayCountFraction: number;
        // We don't have fields to map these
        dayCountFractionMeta: {
            value: number;
            dayCount: number[];
            divideBy: number[];
        },
    }[];
}

export const distinctContactsValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
    const contactIdField = group.get('opsContactId');
    const approverIdField = group.get('opsApproverContactId');
  
    contactIdField.setErrors(null);
    approverIdField.setErrors(null);

    const contactId = contactIdField?.value;
    const approverId = approverIdField?.value;

    if (contactId && approverId && contactId === approverId) {
        contactIdField.setErrors({ sameContat: true });
        approverIdField.setErrors({ sameContat: true });
        return { sameContact: true };
    }

    return null;
  };